import { useEffect, useState } from "react";
import { IAddRelatedWorkItemEntry } from "./useAddRelatedWorkItems";
import { useCentralApi, useCurrentUser } from "hooks/shared";
import { createUrlSearchParams, GridMetaData } from "models/grids";


type UseRelatableWorkItemsResult = [
    IAddRelatedWorkItemEntry[],
    boolean,
    number
];
type UseRelatableWorkItemsSignature = (id: number, workspaceId: number, gridMetadata: GridMetaData, draft?: boolean) => UseRelatableWorkItemsResult;

type RelatableResult = {
    data: IAddRelatedWorkItemEntry[];
    count: number;
}

const useRelatableWorkItems: UseRelatableWorkItemsSignature = (id, workspaceId, gridMetadata, draft) => {
    const [ items, setItems ] = useState<IAddRelatedWorkItemEntry[]>([]);
    const [ loading, setLoading ] = useState<boolean>(false);
    const [ count, setCount ] = useState<number>(0);
    const [ lastQuery, setLastQuery ] = useState<string>('');

    const user = useCurrentUser();
    const fetcher = useCentralApi(user);

    useEffect(() => {
        if (!user || !user.access_token) 
            return;
        if (id === 0 || workspaceId === 0) {
            setItems([]);
            setCount(0);
            setLastQuery('')
            return;
        }

        const searchParams = createUrlSearchParams(gridMetadata);
        const query = `?${searchParams}`;

        if (lastQuery === `${id}/${workspaceId}/${query}`) 
            return;

        const controller = new AbortController();
        (async () => {
            setLoading(true);
            try {
                let url = `/api/v3/workitems/${id}/relateable/${workspaceId}${query}`;
                if (draft) {
                    url += `&draft=${true}`;
                }
                const resp = await fetcher(url, {
                    signal: controller.signal
                });

                const result = await resp.json() as RelatableResult;

                setCount(result.count);
                setItems(result.data);
                
                setLastQuery(`${id}/${workspaceId}/${query}`);
            } finally {
                if (!controller.signal.aborted) {
                    setLoading(false);
                }
            }
        })();

        return () => controller.abort();        
    }, [ user, id, workspaceId, draft, gridMetadata ]);

    return [
        items,
        loading,
        count
    ];
};

export default useRelatableWorkItems;
